import { useSearchParams } from "react-router-dom";
import { Phone, MessageCircle, Clock, CalendarCheck, MailQuestion } from "lucide-react";
import { Reveal, PageHero } from "@/components/bits";
import { BookingForm, EnquiryForm } from "@/components/Forms";
import { BRAND, waLink } from "@/data/site";

const Booking = () => {
  const [params, setParams] = useSearchParams();
  const mode = params.get("mode") === "enquiry" ? "enquiry" : "booking";
  const defaultActivity = params.get("activity") || "";

  return (
    <>
      <PageHero
        eyebrow="Booking & Enquiries"
        title={<>Begin with a <span className="text-red-500">single step</span></>}
        sub="Book an appointment or send an enquiry — our team will review your request and contact you shortly. You can also call or WhatsApp us any day, 6:00 AM to 8:00 PM."
      />

      <section className="mx-auto max-w-[1440px] px-5 pb-24 sm:px-8" data-testid="booking-page">
        <div className="grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="mb-8 grid grid-cols-2 border border-white/12" role="tablist" aria-label="Form type">
              <button
                role="tab"
                aria-selected={mode === "booking"}
                onClick={() => setParams({ mode: "booking" })}
                className={`flex items-center justify-center gap-2 px-4 py-4 font-display text-[11px] font-extrabold uppercase tracking-[0.16em] transition-all ${
                  mode === "booking" ? "bg-red-600 text-white" : "text-zinc-400 hover:text-white"
                }`}
                data-testid="tab-booking"
              >
                <CalendarCheck size={15} /> Book an Appointment
              </button>
              <button
                role="tab"
                aria-selected={mode === "enquiry"}
                onClick={() => setParams({ mode: "enquiry" })}
                className={`flex items-center justify-center gap-2 px-4 py-4 font-display text-[11px] font-extrabold uppercase tracking-[0.16em] transition-all ${
                  mode === "enquiry" ? "bg-red-600 text-white" : "text-zinc-400 hover:text-white"
                }`}
                data-testid="tab-enquiry"
              >
                <MailQuestion size={15} /> Send an Enquiry
              </button>
            </div>

            <Reveal>
              <div className="border border-white/10 bg-[#0F0F14] p-6 sm:p-10">
                {mode === "booking" ? (
                  <BookingForm key={defaultActivity} defaultActivity={defaultActivity} />
                ) : (
                  <EnquiryForm />
                )}
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.12}>
            <aside className="space-y-5">
              <div className="border border-white/10 bg-[#0F0F14] p-7">
                <h3 className="font-display text-sm font-extrabold uppercase tracking-[0.16em] text-white">Prefer to talk?</h3>
                <p className="mt-3 text-[13px] leading-relaxed text-zinc-400">
                  Reach us directly — we're available every day.
                </p>
                <div className="mt-6 space-y-3">
                  <a href={BRAND.phoneLink} className="flex items-center gap-3 border border-white/12 px-4 py-3.5 text-sm font-semibold text-white transition-colors hover:border-red-500 hover:text-red-400" data-testid="booking-side-call">
                    <Phone size={16} className="text-red-500" /> {BRAND.phoneDisplay}
                  </a>
                  <a href={waLink()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 border border-white/12 px-4 py-3.5 text-sm font-semibold text-white transition-colors hover:border-emerald-500 hover:text-emerald-400" data-testid="booking-side-whatsapp">
                    <MessageCircle size={16} className="text-emerald-400" /> {BRAND.whatsappDisplay}
                  </a>
                </div>
              </div>
              <div className="border border-white/10 bg-[#0F0F14] p-7">
                <h3 className="flex items-center gap-2 font-display text-sm font-extrabold uppercase tracking-[0.16em] text-white">
                  <Clock size={15} className="text-red-500" /> Business Hours
                </h3>
                <p className="mt-3 text-[13px] text-zinc-400">{BRAND.hours}</p>
                <p className="mt-5 text-[13px] leading-relaxed text-zinc-500">
                  Two branches across the city — choose whichever is closer when you book.
                </p>
              </div>
            </aside>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Booking;
